/* ================================================================
   Vorschläge — Abgleich zwischen eigenem Kellerbuch und Katalog.

   Statt in jedem Formular einen Haken zu setzen, wird nach jeder
   Änderung abgeglichen: Welche Stammdaten weichen vom Katalog ab oder
   sind dort noch gar nicht bekannt? Daraus entstehen Vorschläge. So
   werden auch Objekte erfasst, die nebenbei entstehen — etwa ein
   neuer Winzer, der im Weinformular mit angelegt wird.

   Herkunft eines Objekts:
     "katalog"  aus dem Katalog übernommen — Änderungen werden
                vorgeschlagen
     "lokal"    vorhanden vor der Katalogeinführung ODER abgelehnt —
                wird NICHT vorgeschlagen
     (fehlt)    neu angelegt — wird als Aufnahme vorgeschlagen

   Ist `abgekoppelt` gesetzt, bleibt das Objekt dauerhaft lokal. Das
   ist der Zustand nach einer Ablehnung: keine weiteren Vorschläge,
   keine Aktualisierung aus dem Katalog.
   ================================================================ */

const TYP_ZU_LISTE = { wein: "weine", winzer: "winzer", region: "regions" };
const TYP_ZU_KATALOG = { wein: "weine", winzer: "winzer", region: "regionen" };

/* Die gemeinsame Form eines Objekts — nur das, was in den Katalog
   gehört. Bestände, Lagerorte, Preise und Favoriten bleiben außen
   vor. Muss der Projektion auf dem Server entsprechen. */
function katalogForm(typ, o) {
  if (typ === "region") {
    return {
      name: o.name || "", land: o.land || "",
      lat: o.lat != null ? o.lat : null, lon: o.lon != null ? o.lon : null,
    };
  }
  if (typ === "winzer") {
    return {
      name: o.name || "", regionId: o.regionId || null,
      ort: o.ort || "", beschreibung: o.beschreibung || "",
    };
  }
  return {
    name: o.name || "", winzerId: o.winzerId || null, regionId: o.regionId || null,
    typ: o.typ || null, appellation: o.appellation || "", beschreibung: o.beschreibung || "",
    rebsortenListe: Array.isArray(o.rebsortenListe) ? o.rebsortenListe : [],
    jahrgaenge: (o.jahrgaenge || []).map((j) => ({
      id: j.id, jahr: j.jahr,
      trinkVon: j.trinkVon != null ? j.trinkVon : null,
      trinkBis: j.trinkBis != null ? j.trinkBis : null,
      /* Analysewerte stehen auf dem Etikett und gehören damit zum
         Wein, nicht zur einzelnen Flasche — deshalb werden sie
         geteilt. Bestand, Lagerort und Preis bleiben privat.
         Alte Katalogeinträge kennen diese Felder nicht; weil der
         Vergleich in offeneVorschlaege BEIDE Seiten durch diese
         Funktion schickt, werden sie dort zu null und lösen keine
         Scheinänderung aus. */
      alkohol: j.alkohol != null ? j.alkohol : null,
      saeure: j.saeure != null ? j.saeure : null,
      restzucker: j.restzucker != null ? j.restzucker : null,
      ph: j.ph != null ? j.ph : null,
      schwefel: j.schwefel || "",
      /* Das eigene Etikett wird vorgeschlagen; ein bereits aus dem
         Katalog übernommenes bleibt unverändert bestehen. */
      foto: j.foto ? { pfad: j.foto.pfad, thumb: j.foto.thumb || j.foto.pfad }
        : (j.katalogFoto || null),
    })),
  };
}

/* Vergleich über eine stabile Zeichenkette: Schlüssel sortiert,
   damit die Reihenfolge im Objekt keine Rolle spielt. */
function stabil(wert) {
  if (Array.isArray(wert)) return "[" + wert.map(stabil).join(",") + "]";
  if (wert && typeof wert === "object") {
    return "{" + Object.keys(wert).sort().map((k) => JSON.stringify(k) + ":" + stabil(wert[k])).join(",") + "}";
  }
  return JSON.stringify(wert === undefined ? null : wert);
}

const gleich = (a, b) => stabil(a) === stabil(b);

/* Bezüge eines Objekts auf andere Stammdaten. */
function bezuege(typ, o) {
  if (typ === "wein") {
    return [o.winzerId ? ["winzer", o.winzerId] : null, o.regionId ? ["region", o.regionId] : null].filter(Boolean);
  }
  if (typ === "winzer") return o.regionId ? [["region", o.regionId]] : [];
  return [];
}

/* Kann ein Bezug im Katalog ankommen? Ja, wenn er schon dort steht
   oder selbst vorgeschlagen werden kann. Nein bei lokal geführten oder
   abgekoppelten Objekten — ein Wein mit einem solchen Winzer wäre im
   Katalog ein kaputter Eintrag, und der Server nähme ihn ohnehin
   nicht an. Ein Bezug, den weder Kellerbuch noch Katalog kennen, hält
   nichts zurück: Daran kann der Nutzer nichts ändern, und der Server
   meldet es dem Admin. */
function bezugErreichbar(db, katalog, typ, id) {
  if ((katalog[TYP_ZU_KATALOG[typ]] || []).some((o) => o.id === id)) return true;
  const eigen = (db[TYP_ZU_LISTE[typ]] || []).find((o) => o.id === id);
  if (!eigen) return true;
  if (eigen.herkunft === "lokal" || eigen.abgekoppelt) return false;
  return bezuege(typ, eigen).every(([t2, id2]) => bezugErreichbar(db, katalog, t2, id2));
}

/** Objekte, die vorgeschlagen würden, aber zurückgehalten werden, weil
    ein Bezug den Katalog nicht erreichen kann. Für die Anzeige unter
    „Meine Vorschläge“ — sonst geschähe schlicht nichts. */
function zurueckgehalten(db, katalog) {
  if (!db || !katalog) return [];
  const raus = [];
  for (const typ of ["winzer", "wein"]) {
    for (const o of db[TYP_ZU_LISTE[typ]] || []) {
      if (o.abgekoppelt || o.herkunft === "lokal" || o.nurKatalog) continue;
      const fehlt = bezuege(typ, o).filter(([t2, id2]) => !bezugErreichbar(db, katalog, t2, id2));
      if (fehlt.length === 0) continue;
      raus.push({
        typ, id: o.id, name: o.name || "",
        fehlt: fehlt.map(([t2, id2]) => {
          const b = (db[TYP_ZU_LISTE[t2]] || []).find((x) => x.id === id2);
          return { typ: t2, id: id2, name: b ? b.name : null };
        }),
      });
    }
  }
  return raus;
}

/* Ermittelt, welche Vorschläge fällig sind. Liefert eine Liste, die
   der Aufrufer nacheinander an den Server schickt. */
function offeneVorschlaege(db, katalog, vorhandene, gesperrt = null) {
  if (!katalog) return [];
  /* Eine abgelehnte AUFNAHME schließt das Objekt aus. Eine abgelehnte
     ÄNDERUNG sperrt nur genau die abgelehnte Fassung — ändert der
     Nutzer weiter, darf das wieder vorgelegt werden. Früher sperrte
     jede Ablehnung das Objekt für immer. */
  const aufnahmeAbgelehnt = new Set(vorhandene
    .filter((v) => v.status === "abgelehnt" && v.art === "neu").map((v) => v.objektId));
  const abgelehnteFassungen = new Map();
  for (const v of vorhandene) {
    if (v.status !== "abgelehnt" || v.art === "neu") continue;
    if (!abgelehnteFassungen.has(v.objektId)) abgelehnteFassungen.set(v.objektId, []);
    abgelehnteFassungen.get(v.objektId).push(v.nachher);
  }
  const offenGleich = new Map(
    vorhandene.filter((v) => v.status === "offen").map((v) => [v.objektId, v.nachher]));

  const faellig = [];
  for (const typ of ["region", "winzer", "wein"]) {
    const eigene = db[TYP_ZU_LISTE[typ]] || [];
    const imKatalog = new Map((katalog[TYP_ZU_KATALOG[typ]] || []).map((o) => [o.id, o]));
    for (const o of eigene) {
      if (o.abgekoppelt || aufnahmeAbgelehnt.has(o.id)) continue;
      if (o.herkunft === "lokal") continue;
      /* Solange eine Dublettenfrage offen ist, wird nichts
         vorgeschlagen — der Nutzer entscheidet zuerst. */
      if (gesperrt && gesperrt.has(o.id)) continue;
      if (!bezuege(typ, o).every(([t2, id2]) => bezugErreichbar(db, katalog, t2, id2))) continue;

      const form = katalogForm(typ, o);
      if ((abgelehnteFassungen.get(o.id) || []).some((f) => gleich(katalogForm(typ, f), form))) continue;
      const k = imKatalog.get(o.id);

      if (o.herkunft === "katalog") {
        if (!k) continue;                       /* Katalog kennt es (noch) nicht */
        if (gleich(katalogForm(typ, k), form)) continue;   /* unverändert */
        if (offenGleich.has(o.id) && gleich(offenGleich.get(o.id), form)) continue;
        faellig.push({ art: "aenderung", typ, objektId: o.id, nachher: form });
        continue;
      }

      /* Ohne Herkunft: neu angelegt. */
      if (k && gleich(katalogForm(typ, k), form)) continue;
      if (offenGleich.has(o.id) && gleich(offenGleich.get(o.id), form)) continue;
      faellig.push({ art: k ? "aenderung" : "neu", typ, objektId: o.id, nachher: form });
    }
  }
  return faellig;
}

/* ================================================================
   Altbestand freigeben

   Alles, was beim Einführen des Katalogs schon im Kellerbuch lag,
   trägt `herkunft: "lokal"` und wird nie vorgeschlagen. Das ist als
   Schutz gedacht — sonst hätte der erste Cloud-Nutzer dem Admin auf
   einen Schlag sein halbes Kellerbuch vorgelegt.

   Wer diese Weine aber gemeinsam pflegen will, braucht einen Weg
   zurück. Freigeben heißt: `herkunft` fällt weg, das Objekt gilt
   wieder als neu, und der reguläre Abgleich erfasst es beim nächsten
   Durchlauf.

   Zwei Dinge macht das nicht:

   - `abgekoppelt` bleibt unangetastet. Das ist der Zustand nach einer
     Ablehnung, und der Server weist einen erneuten Vorschlag ohnehin
     mit 409 ab. Eine Freigabe, die nichts bewirkt, wäre schlimmer als
     keine — sie sähe aus wie Erfolg.
   - Es prüft nicht auf Dubletten im Katalog. Liegt derselbe Wein dort
     schon unter anderer ID, entsteht ein zweiter Eintrag; dafür gibt
     es das Zusammenführen.
   ================================================================ */

/** Was lokal geführt wird — getrennt nach freigebbar und dauerhaft
    abgekoppelt, weil nur das eine sich ändern lässt. */
function lokalGefuehrte(db) {
  const raus = { region: [], winzer: [], wein: [], abgekoppelt: 0 };
  for (const typ of ["region", "winzer", "wein"]) {
    for (const o of db[TYP_ZU_LISTE[typ]] || []) {
      if (o.herkunft !== "lokal") continue;
      if (o.abgekoppelt) { raus.abgekoppelt++; continue; }
      raus[typ].push(o);
    }
  }
  raus.gesamt = raus.region.length + raus.winzer.length + raus.wein.length;
  return raus;
}


/**
 * Gibt lokal geführte Objekte für den Katalog frei.
 *
 * `ids` ist eine Menge von Objekt-IDs; ohne Angabe wird alles
 * freigegeben, was freigebbar ist.
 *
 * Winzer und Region eines freigegebenen Weins gehen IMMER mit —
 * andernfalls stünde im Katalog ein Wein, dessen Winzer dort niemand
 * kennt, und `uebernehmeWein` fände beim nächsten Nutzer nichts zum
 * Mitnehmen. Ein halb freigegebener Wein wäre kein halber Gewinn,
 * sondern ein kaputter Eintrag.
 */
function gibFrei(db, ids = null) {
  const offen = lokalGefuehrte(db);
  const freigebbar = new Set([...offen.region, ...offen.winzer, ...offen.wein].map((o) => o.id));
  /* Nur wirklich Freigebbares zählt — sonst zöge ein abgekoppelter
     Winzer, der selbst gar nicht freigegeben wird, seine Region mit
     in den Katalog. */
  const gewaehlt = new Set((ids ? [...ids] : [...freigebbar]).filter((id) => freigebbar.has(id)));
  if (gewaehlt.size === 0) return db;

  const winzerById = new Map((db.winzer || []).map((o) => [o.id, o]));

  /* Abhängigkeiten nachziehen. */
  const mit = new Set(gewaehlt);
  const nimmMit = (id) => { if (id) mit.add(id); };
  for (const w of db.weine || []) {
    if (!gewaehlt.has(w.id)) continue;
    nimmMit(w.regionId);
    nimmMit(w.winzerId);
    const wz = winzerById.get(w.winzerId);
    if (wz) nimmMit(wz.regionId);
  }
  for (const wz of db.winzer || []) {
    if (gewaehlt.has(wz.id)) nimmMit(wz.regionId);
  }

  let veraendert = false;
  const frei = (liste) => (liste || []).map((o) => {
    if (!mit.has(o.id) || o.herkunft !== "lokal" || o.abgekoppelt) return o;
    veraendert = true;
    const { herkunft, ...rest } = o;
    return rest;
  });

  const neu = {
    ...db,
    regions: frei(db.regions),
    winzer: frei(db.winzer),
    weine: frei(db.weine),
  };
  return veraendert ? neu : db;
}


/* Wendet entschiedene Vorschläge auf das eigene Kellerbuch an:
   angenommen          → das Objekt gehört zum Katalog;
   Aufnahme abgelehnt  → dauerhaft lokal, nie wieder vorschlagen;
   Änderung abgelehnt  → das Objekt bleibt mit dem Katalog verbunden.
                         Die eigene Fassung bleibt stehen, nur diese
                         Fassung wird nicht erneut vorgelegt.

   Bis Oktober 2026 koppelte auch eine abgelehnte Änderung das ganze
   Objekt ab — ein Katalogwein bekam dann nach einer abgelehnten
   Kleinigkeit nie wieder eine Aktualisierung. Solche Objekte werden
   hier wieder angebunden: Es gibt keinen anderen Weg, auf dem ein
   Objekt mit einer abgelehnten Änderung abgekoppelt sein könnte. */
function wendeEntscheidungenAn(db, vorschlaege) {
  const angenommen = new Set(vorschlaege.filter((v) => v.status === "angenommen").map((v) => v.objektId));
  const aufnahmeAbgelehnt = new Set(vorschlaege
    .filter((v) => v.status === "abgelehnt" && v.art === "neu").map((v) => v.objektId));
  const aenderungAbgelehnt = new Set(vorschlaege
    .filter((v) => v.status === "abgelehnt" && v.art !== "neu").map((v) => v.objektId));
  if (angenommen.size === 0 && aufnahmeAbgelehnt.size === 0 && aenderungAbgelehnt.size === 0) return db;

  let veraendert = false;
  const bearbeite = (liste) => liste.map((o) => {
    if (aufnahmeAbgelehnt.has(o.id)) {
      if (o.abgekoppelt && o.herkunft === "lokal") return o;
      veraendert = true;
      return { ...o, herkunft: "lokal", abgekoppelt: true };
    }
    if (aenderungAbgelehnt.has(o.id) && o.abgekoppelt && o.herkunft === "lokal") {
      veraendert = true;
      const { abgekoppelt, ...rest } = o;
      return { ...rest, herkunft: "katalog" };
    }
    if (angenommen.has(o.id) && o.herkunft !== "katalog" && !o.abgekoppelt) {
      veraendert = true;
      return { ...o, herkunft: "katalog" };
    }
    return o;
  });

  const neu = {
    ...db,
    regions: bearbeite(db.regions || []),
    winzer: bearbeite(db.winzer || []),
    weine: bearbeite(db.weine || []),
  };
  return veraendert ? neu : db;
}

async function vorschlaegeHolen() {
  const r = await fetch("/api/vorschlaege");
  if (!r.ok) throw new Error("Vorschläge konnten nicht geladen werden.");
  return (await r.json()).vorschlaege || [];
}

async function vorschlagSenden(v) {
  const r = await fetch("/api/vorschlaege", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(v),
  });
  if (r.status === 409) return null;      /* abgelehnt oder lokaler Modus — still übergehen */
  if (!r.ok) throw new Error("Der Vorschlag konnte nicht gesendet werden.");
  return await r.json();
}

/** Mehrere Vorschläge in einer Anfrage. Liefert
    { gesendet, gesperrt, angenommen } — `angenommen` zählt, was der
    Server gleich übernommen hat (Vorschläge des Admins). */
async function vorschlaegeSenden(liste) {
  if (!liste || liste.length === 0) return { gesendet: 0, gesperrt: 0, angenommen: 0 };
  const r = await fetch("/api/vorschlaege", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ liste }),
  });
  if (r.status === 409) return { gesendet: 0, gesperrt: 0, angenommen: 0 }; /* lokaler Modus */
  if (r.status === 401) throw Object.assign(new Error("Nicht angemeldet."), { status: 401 });
  if (!r.ok) throw new Error("Die Vorschläge konnten nicht gesendet werden.");
  const j = await r.json();
  const erg = j.ergebnisse || [];
  return {
    gesendet: erg.filter((e) => e.id).length,
    gesperrt: erg.filter((e) => e.gesperrt).length,
    angenommen: j.angenommen || 0,
  };
}

/* Offenen Vorschlag zurückziehen. Das Kellerbuch muss VORHER so
   gestellt sein, dass der Abgleich ihn nicht sofort erneut erzeugt
   (siehe `nachZurueckziehen`). */
async function vorschlagZurueckziehen(id) {
  const r = await fetch("/api/vorschlaege/" + encodeURIComponent(id), { method: "DELETE" });
  if (r.status === 404) return false;      /* inzwischen entschieden */
  if (!r.ok) throw new Error("Der Vorschlag konnte nicht zurückgezogen werden.");
  return true;
}

/** Wie das Kellerbuch nach dem Zurückziehen aussieht:
    Aufnahme  → das Objekt wird lokal geführt (`herkunft: "lokal"`,
                ohne `abgekoppelt` — es lässt sich unter „Lokal
                geführt“ später wieder freigeben).
    Änderung  → das Objekt wird auf die Katalogfassung zurückgesetzt;
                das übernimmt der Aufrufer mit `aufKatalogfassung`,
                weil dafür das Katalogobjekt gebraucht wird. */
function alsLokalGefuehrt(db, typ, objektId) {
  const feld = TYP_ZU_LISTE[typ];
  if (!feld) return db;
  let veraendert = false;
  const liste = (db[feld] || []).map((o) => {
    if (o.id !== objektId || o.herkunft === "lokal") return o;
    veraendert = true;
    return { ...o, herkunft: "lokal" };
  });
  return veraendert ? { ...db, [feld]: liste } : db;
}

/* ── Geteilte Kritiken zum Server ───────────────────────────────
   Wird nach dem Abgleich aufgerufen. Ist das Teilen abgeschaltet,
   geht eine leere Liste hinauf — das löscht den Eintrag des Kontos
   im gemeinsamen Bestand. Genau so soll ein Widerruf wirken: nicht
   „ab jetzt nichts Neues mehr“, sondern „weg damit“.

   Übertragen werden nur Kritiken mit Wein; offene (noch nicht
   zugeordnete) bleiben privat, sie sagen ohne Wein niemandem etwas. */
async function kritikenTeilen(db, geteilt) {
  const liste = geteilt
    ? (db.kritiken || []).filter((k) => k.weinId).map((k) => ({
        id: k.id, weinId: k.weinId, jgId: k.jgId || null, jahr: k.jahr != null ? k.jahr : null,
        kritiker: k.kritiker || "", schema: k.schema || null,
        wert: k.wert != null ? k.wert : null,
        /* Kein `anlass` — der bleibt privat (siehe KRITIK_FELDER in
           server.js). Der Server verwürfe ihn ohnehin; hier gar nicht
           erst zu senden ist die ehrlichere Stelle. */
        pairing: k.pairing || "", text: k.text || "",
        erfasst: k.erfasst || null,
      }))
    : [];
  const r = await fetch("/api/kritiken", {
    method: "PUT", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kritiken: liste }),
  });
  if (!r.ok) throw new Error("Die Kritiken konnten nicht geteilt werden.");
  return (await r.json()).anzahl;
}


export {
  TYP_ZU_KATALOG, TYP_ZU_LISTE, alsLokalGefuehrt, gibFrei, gleich, katalogForm, kritikenTeilen, lokalGefuehrte,
  offeneVorschlaege, vorschlaegeHolen, vorschlaegeSenden, vorschlagSenden, vorschlagZurueckziehen, wendeEntscheidungenAn, zurueckgehalten,
};
